import React, { useMemo } from "react";
import {
  Badge,
  Box,
  Code,
  Divider,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import {
  DROFUS_INTERFACE_OCCURRENCE_DERIVED_NEED_RULES,
  DROFUS_INTERFACE_OCCURRENCE_FIELD_GROUPS,
  DROFUS_INTERFACE_OCCURRENCE_FIELD_META,
  DROFUS_INTERFACE_OCCURRENCE_J_ROOMS_FILTER,
  DROFUS_INTERFACE_OCCURRENCE_NEED_DISCIPLINE_BY_PATH,
  DROFUS_INTERFACE_OCCURRENCE_NEED_RULES,
  DROFUS_INTERFACE_OCCURRENCE_NEED_TRUTHY_RULES,
  DROFUS_INTERFACE_OCCURRENCE_SELECT,
  DROFUS_INTERFACE_OCCURRENCE_SELECT_FIELDS,
} from "@multiconsult-group/multiplacer-core";

type DrofusMappingLogicModalProps = {
  opened: boolean;
  onClose: () => void;
};

const groupLabelById: Record<string, string> = {
  occurrence: "Forekomst",
  room: "Rom",
  article: "Artikkel",
  system: "System",
  tfmStatus: "TFM/status",
  occurrenceData: "Forekomstdata",
  articleNeeds: "Artikkelbehov",
};

const disciplineColorById: Record<string, string> = {
  RIE: "yellow",
  RIKT: "blue",
  RIAut: "grape",
  RIV: "teal",
};

const disciplineTextColorById: Record<string, string> = {
  RIE: "yellow.9",
  RIKT: "blue.8",
  RIAut: "grape.8",
  RIV: "teal.8",
};

const compactTd: React.CSSProperties = {
  verticalAlign: "top",
  paddingTop: 6,
  paddingBottom: 6,
};

const compactTableStyle: React.CSSProperties = {
  tableLayout: "fixed",
};

const SourceFieldsInline: React.FC<{ fieldIds: readonly string[] }> = ({ fieldIds }) => (
  <Box style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    {fieldIds.map((fieldId) => {
      const displayName =
        DROFUS_INTERFACE_OCCURRENCE_FIELD_META.find((field) => field.id === fieldId)
          ?.displayName ?? "";

      return (
        <Text key={fieldId} size="xs" lh={1.25}>
          <Code fz={10}>{fieldId}</Code>
          <Text span c="dimmed">
            {" "}
            {displayName}
          </Text>
        </Text>
      );
    })}
  </Box>
);

const DisciplineBadge: React.FC<{ discipline: string }> = ({ discipline }) => (
  <Badge
    color={disciplineColorById[discipline] ?? "gray"}
    c={disciplineTextColorById[discipline] ?? "gray.8"}
    variant="light"
    radius="sm"
    styles={{
      root: {
        border: "1px solid currentColor",
      },
      label: {
        color: "inherit",
      },
    }}
  >
    {discipline}
  </Badge>
);

export const DrofusMappingLogicModal: React.FC<DrofusMappingLogicModalProps> = ({
  opened,
  onClose,
}) => {
  const fieldUsageById = useMemo(() => {
    const usage = new Map<string, Array<{ needKey: string; label: string; discipline: string }>>();

    for (const [needKey, rule] of Object.entries(DROFUS_INTERFACE_OCCURRENCE_NEED_RULES)) {
      for (const fieldId of rule.sourceFieldIds) {
        const current = usage.get(fieldId) ?? [];
        current.push({
          needKey,
          label: rule.label,
          discipline: rule.affectedDiscipline,
        });
        usage.set(fieldId, current);
      }
    }

    return usage;
  }, []);

  const fieldsByGroup = useMemo(
    () =>
      DROFUS_INTERFACE_OCCURRENCE_FIELD_GROUPS.map((group) => ({
        group,
        fields: DROFUS_INTERFACE_OCCURRENCE_FIELD_META.filter((field) => field.group === group),
      })),
    []
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="dRofus mappinglogikk"
      size="95vw"
      centered
      styles={{
        body: { paddingTop: 0 },
        content: { height: "90vh" },
      }}
    >
      <ScrollArea.Autosize mah="calc(90vh - 84px)" type="auto" offsetScrollbars>
        <Stack gap="md" pb="md">
          <Paper withBorder radius="sm" p="md">
            <Stack gap="xs">
              <Group justify="space-between" align="flex-start">
                <Box>
                  <Title order={5}>API-oppsett</Title>
                  <Text size="sm" c="dimmed">
                    Dette er feltene og filteret som brukes når forekomster hentes fra dRofus.
                  </Text>
                </Box>
                <Badge variant="light" color="blue" radius="sm">
                  {DROFUS_INTERFACE_OCCURRENCE_SELECT_FIELDS.length} felt
                </Badge>
              </Group>
              <Text size="sm" fw={600}>
                $filter
              </Text>
              <Code block>{DROFUS_INTERFACE_OCCURRENCE_J_ROOMS_FILTER}</Code>
              <Text size="sm" fw={600}>
                $select
              </Text>
              <Code block>{DROFUS_INTERFACE_OCCURRENCE_SELECT}</Code>
            </Stack>
          </Paper>

          <Paper withBorder radius="sm" p="md">
            <Stack gap="sm">
              <Title order={5}>Behovslogikk</Title>
              <Text size="sm" c="dimmed">
                Et felt regnes som aktivt behov etter disse reglene. Hvis et behov blir aktivt,
                settes tilhørende fag på forekomsten.
              </Text>
              <Group gap="xs">
                {DROFUS_INTERFACE_OCCURRENCE_NEED_TRUTHY_RULES.map((rule) => (
                  <Badge key={rule} variant="light" color="gray" radius="sm">
                    {rule}
                  </Badge>
                ))}
              </Group>

              <Table striped highlightOnHover withColumnBorders style={compactTableStyle}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 180 }}>Behov</Table.Th>
                    <Table.Th style={{ width: 220 }}>Normalisert nøkkel</Table.Th>
                    <Table.Th style={{ width: 90 }}>Fag</Table.Th>
                    <Table.Th>Kildefelt</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {Object.entries(DROFUS_INTERFACE_OCCURRENCE_NEED_RULES).map(([needKey, rule]) => (
                    <Table.Tr key={needKey}>
                      <Table.Td style={compactTd}>
                        <Text fw={600}>{rule.label}</Text>
                        <Text size="xs" c="dimmed">
                          {needKey}
                        </Text>
                      </Table.Td>
                      <Table.Td style={compactTd}>
                        <Code>{rule.normalizedPath}</Code>
                      </Table.Td>
                      <Table.Td style={compactTd}>
                        <DisciplineBadge discipline={rule.affectedDiscipline} />
                      </Table.Td>
                      <Table.Td style={compactTd}>
                        <SourceFieldsInline fieldIds={rule.sourceFieldIds} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>

              <Divider />

              <Title order={6}>Avledede behov</Title>
              <Table withColumnBorders style={compactTableStyle}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 180 }}>Behov</Table.Th>
                    <Table.Th style={{ width: 220 }}>Normalisert nøkkel</Table.Th>
                    <Table.Th style={{ width: 90 }}>Fag</Table.Th>
                    <Table.Th>Bygges fra</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {Object.entries(DROFUS_INTERFACE_OCCURRENCE_DERIVED_NEED_RULES).map(
                    ([needKey, rule]) => (
                      <Table.Tr key={needKey}>
                        <Table.Td style={compactTd}>
                          <Text fw={600}>{rule.label}</Text>
                          <Text size="xs" c="dimmed">
                            {needKey}
                          </Text>
                        </Table.Td>
                        <Table.Td style={compactTd}>
                          <Code>{rule.normalizedPath}</Code>
                        </Table.Td>
                        <Table.Td style={compactTd}>
                          <DisciplineBadge discipline={rule.affectedDiscipline} />
                        </Table.Td>
                        <Table.Td style={compactTd}>
                          <Group gap={4}>
                            {rule.sourceNeedKeys.map((sourceNeedKey) => (
                              <Badge key={sourceNeedKey} variant="light" color="gray" radius="sm">
                                {sourceNeedKey}
                              </Badge>
                            ))}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    )
                  )}
                </Table.Tbody>
              </Table>
            </Stack>
          </Paper>

          <Paper withBorder radius="sm" p="md">
            <Stack gap="sm">
              <Title order={5}>Normaliserte behov → fag</Title>
              <Table withColumnBorders style={compactTableStyle}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Normalisert nøkkel</Table.Th>
                    <Table.Th style={{ width: 90 }}>Fag</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {Object.entries(DROFUS_INTERFACE_OCCURRENCE_NEED_DISCIPLINE_BY_PATH).map(
                    ([path, discipline]) => (
                      <Table.Tr key={path}>
                        <Table.Td style={compactTd}>
                          <Code>{path}</Code>
                        </Table.Td>
                        <Table.Td style={compactTd}>
                          <DisciplineBadge discipline={discipline} />
                        </Table.Td>
                      </Table.Tr>
                    )
                  )}
                </Table.Tbody>
              </Table>
            </Stack>
          </Paper>

          <Paper withBorder radius="sm" p="md">
            <Stack gap="md">
              <Group justify="space-between">
                <Box>
                  <Title order={5}>Alle felt fra API-et</Title>
                  <Text size="sm" c="dimmed">
                    Viser rått felt, displaynavn, feltgruppe, normalisert nøkkel og eventuell
                    behovs-/fagkobling.
                  </Text>
                </Box>
                <Badge variant="light" color="gray" radius="sm">
                  Alt synlig
                </Badge>
              </Group>

              {fieldsByGroup.map(({ group, fields }) => (
                <Stack key={group} gap="xs">
                  <Group gap="xs">
                    <Title order={6}>{groupLabelById[group] ?? group}</Title>
                    <Badge variant="light" color="gray" radius="sm">
                      {fields.length}
                    </Badge>
                  </Group>
                  <Table striped highlightOnHover withColumnBorders style={compactTableStyle}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: 300 }}>API-felt</Table.Th>
                        <Table.Th>Displayname</Table.Th>
                        <Table.Th style={{ width: 260 }}>Normalisert nøkkel</Table.Th>
                        <Table.Th style={{ width: 280 }}>Behovslogikk</Table.Th>
                        <Table.Th style={{ width: 90 }}>Fag</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {fields.map((field) => {
                        const usages = fieldUsageById.get(field.id) ?? [];

                        return (
                          <Table.Tr key={field.id}>
                            <Table.Td style={compactTd}>
                              <Code>{field.id}</Code>
                            </Table.Td>
                            <Table.Td style={compactTd}>{field.displayName}</Table.Td>
                            <Table.Td style={compactTd}>
                              {field.normalizedPath ? <Code>{field.normalizedPath}</Code> : "-"}
                            </Table.Td>
                            <Table.Td style={compactTd}>
                              {usages.length > 0 ? (
                                <Stack gap={2}>
                                  {usages.map((usage) => (
                                    <Group key={`${field.id}-${usage.needKey}`} gap={4}>
                                      <Badge variant="light" color="gray" radius="sm">
                                        {usage.needKey}
                                      </Badge>
                                      <Text size="xs">{usage.label}</Text>
                                    </Group>
                                  ))}
                                </Stack>
                              ) : (
                                <Text size="sm" c="dimmed">
                                  -
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td style={compactTd}>
                              {usages.length > 0 ? (
                                <Group gap={4}>
                                  {[...new Set(usages.map((usage) => usage.discipline))].map(
                                    (discipline) => (
                                      <DisciplineBadge key={discipline} discipline={discipline} />
                                    )
                                  )}
                                </Group>
                              ) : (
                                <Text size="sm" c="dimmed">
                                  -
                                </Text>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Stack>
      </ScrollArea.Autosize>
    </Modal>
  );
};
